
import { Dialog, DialogContent, DialogFooter } from 'components/ui/dialog'
import { Textarea } from 'components/ui/textarea'
import { Button } from 'components/ui/button';
import { useAddInstructionToProject } from '../hooks/use-projects';
import { useState, useEffect, useRef, useCallback } from 'react';

import { useToast } from "../../../hooks/use-toast";
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

/** Same threshold as chat shell — keyboard (or chrome) has shrunk the visible area. */
const KEYBOARD_OPEN_DELTA_PX = 150;

function clearDialogKeyboardPin(el: HTMLElement) {
    el.style.top = '';
    el.style.left = '';
    el.style.transform = '';
    el.style.maxHeight = '';
}

function syncDialogToKeyboard(el: HTMLElement) {
    const vv = window.visualViewport;
    if (!vv) {
        clearDialogKeyboardPin(el);
        return false;
    }

    const keyboardOpen = window.innerHeight - vv.height > KEYBOARD_OPEN_DELTA_PX;
    if (!keyboardOpen) {
        clearDialogKeyboardPin(el);
        return false;
    }

    const margin = 8;
    el.style.top = `${vv.offsetTop + margin}px`;
    el.style.left = `${vv.offsetLeft + vv.width / 2}px`;
    el.style.transform = 'translateX(-50%) translateY(0)';
    el.style.maxHeight = `${Math.max(200, vv.height - margin * 2)}px`;
    return true;
}

function AddInstructionModal({ isFetchingProject, fetchedProject, isOpen, setIsOpen, projectId, refetchProject }: {
    isFetchingProject: boolean,
    fetchedProject: any,
    isOpen: boolean, setIsOpen: (isOpen: boolean) => void, projectId: string,
    refetchProject: () => void,
}) {
    console.log('fetchedProject prompts', fetchedProject?.prompts);
    const { toast } = useToast();
    const { t } = useTranslation();

    const { mutateAsync: addInstructionToProject, isPending } = useAddInstructionToProject(projectId);
    const [instruction, setInstruction] = useState('');
    const contentRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Update instruction when modal opens or prompts change
    useEffect(() => {
        if (isOpen && fetchedProject?.prompts?.length > 0 && !isFetchingProject) {
            const prompt = fetchedProject?.prompts?.find((prompt: any) => prompt.type === 'user');
            setInstruction(prompt?.content || '');
        }
    }, [isOpen, fetchedProject, isFetchingProject]);

    // Follow the keyboard: move into the visual viewport while open, restore when it closes.
    useEffect(() => {
        if (!isOpen) return undefined;

        const sync = () => {
            const el = contentRef.current;
            if (!el) return;
            const keyboardOpen = syncDialogToKeyboard(el);
            if (keyboardOpen) {
                requestAnimationFrame(() => {
                    textareaRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                });
            }
        };

        sync();

        const vv = window.visualViewport;
        vv?.addEventListener('resize', sync);
        vv?.addEventListener('scroll', sync);
        window.addEventListener('resize', sync);

        return () => {
            vv?.removeEventListener('resize', sync);
            vv?.removeEventListener('scroll', sync);
            window.removeEventListener('resize', sync);
            if (contentRef.current) {
                clearDialogKeyboardPin(contentRef.current);
            }
        };
    }, [isOpen]);

    const keepCaretVisible = useCallback(() => {
        requestAnimationFrame(() => {
            const el = contentRef.current;
            if (el) {
                syncDialogToKeyboard(el);
            }
            textareaRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
    }, []);

    const handleAddInstruction = async () => {
        try {
            const response = await addInstructionToProject(instruction);
            // console.log('response', response);
            if (response?.prompts && response.prompts.length > 0) {
                toast({
                    title: t('INSTRUCTION_ADDED_SUCCESSFULLY'),
                    description: t('INSTRUCTION_DESCRIPTION_SUCCESSFULLY'),
                    variant: 'success',
                });
                setInstruction('');
                setIsOpen(false);
                await refetchProject();
            } else {
                toast({
                    title: t('FAILED'),
                    description: t('FAILED_TO_ADD_INSTRUCTION'),
                    variant: 'destructive',
                });
            }
        } catch (error) {
            console.error('Failed to add instruction:', error);
            toast({
                title: t('FAILED'),
                description: error instanceof Error ? error.message : t('FAILED_TO_ADD_INSTRUCTION'),
                variant: 'destructive',
            });
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent
                ref={contentRef}
                className="flex max-h-[90dvh] w-[calc(100%-2rem)] max-w-4xl flex-col overflow-hidden p-4 sm:w-[calc(100%-3rem)] sm:max-w-[800px]"
                showOverlay={true}
                hideClose={true}
            >
                {isFetchingProject ? (
                    <div className="flex h-40 w-full items-center justify-center">
                        <Loader2 className="animate-spin" />
                    </div>
                ) : (
                    <div className="flex min-h-0 flex-1 flex-col">
                        <div className="min-h-0 flex-1 overflow-y-auto">
                            <h2 className="text-[16px] font-light leading-[24px] text-foreground sm:text-[18px] sm:leading-[28px]">
                                {t('INSTRUCTIONS')}
                            </h2>

                            <h5 className="mb-2 mt-5 text-sm font-normal">
                                {t('HOW_CAN_APP_BEST_HELP_YOU_WITH_THIS_PROJECT')}
                            </h5>
                            <p className="text-sm font-light text-muted-foreground">
                                {t('YOU_CAN_ASK_APP')}
                            </p>
                            <Textarea
                                ref={textareaRef}
                                className="border-heavy mb-4 mt-4 min-h-[140px] w-full resize-none overflow-x-clip overflow-y-auto rounded-lg border bg-transparent px-3 py-2 focus:ring-1 focus:ring-token-text-primary sm:min-h-64"
                                placeholder={t('RESPOND_IN_SPANISH_REFERENCE')}
                                value={instruction}
                                onChange={(e) => {
                                    setInstruction(e.target.value);
                                    keepCaretVisible();
                                }}
                                onFocus={keepCaretVisible}
                            />
                        </div>

                        <DialogFooter className="flex shrink-0 flex-row justify-end gap-2 border-t border-grey-100 pt-4 dark:border-border">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setInstruction('');
                                    setIsOpen(false);
                                }}
                                disabled={isPending}
                                className="w-24 outline-none"
                            >
                                {t('CANCEL')}
                            </Button>
                            <Button
                                onClick={() => {
                                    handleAddInstruction();
                                }}
                                className="w-24 bg-primary"
                                disabled={isPending && isFetchingProject}
                            >
                                {(isPending || isFetchingProject) ? <Loader2 className="animate-spin" /> : t('SAVE')}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
export default AddInstructionModal;
